import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { formatEventDate } from '@root/src/utils/dates';
import { REGISTRATION_STATUS } from '@root/src/types';
import getRoute from '@root/src/services/routeList';
import Button, { BUTTON_VARIANT } from '@features/ui/components/Button';
import Icon, { ICON_NAME } from '@features/ui/components/Icon';
import StackableCard, { StackableCardSection } from '@features/ui/components/StackableCard';
import { textP, textSmall } from '@features/ui/styles/textStyles';
import { selectSerieDetail } from '../slices/serieDetailServerSlice';

interface SerieSidePanelSectionProps {
  icon?: ICON_NAME;
  title?: string;
  children?: React.ReactNode;
}

const SerieSidePanelSection = ({ icon, title, children }: SerieSidePanelSectionProps) => (
  <SectionContainer>
    {icon && <SectionIcon name={icon} color="var(--color-grey3)" size="20px" />}
    {title && <SectionTitle>{title}</SectionTitle>}
    <SectionContent>{children}</SectionContent>
  </SectionContainer>
);

interface SerieSidePanelProps {
  className?: string;
}

const SerieSidePanel = ({ className }: SerieSidePanelProps) => {
  const { t } = useTranslation();

  const {
    serieDates,
    status,
    locations,
    serieType,
    slug,
    previousSerieSlug,
    previousSerieLabel,
    urlOrganiser,
  } = useSelector(selectSerieDetail);

  return (
    <CardContainer className={className}>
      <SectionsContainer>
        {serieDates && (
          <SerieSidePanelSection icon={ICON_NAME.dateRange} title={t('serieSidePanel_sectionDateTitle.message')}>
            {formatEventDate(null, serieDates.earliestEventDate, serieDates.latestEventDate)}
          </SerieSidePanelSection>
        )}
        {locations && (
          <SerieSidePanelSection icon={ICON_NAME.pin} title={t('serieSidePanel_sectionLocationTitle.message')}>
            {locations.join(', ')}
          </SerieSidePanelSection>
        )}
        {serieType && (
          <SerieSidePanelSection icon={ICON_NAME.sort} title={t('serieSidePanel_sectionSportTitle.message')}>
            {serieType}
          </SerieSidePanelSection>
        )}
        <SerieSidePanelSection icon={ICON_NAME.playlistAddCheck} title={t('serieSidePanel_sectionSubscriptionsTitle.message')}>
          {status.registrationFrom && (
            <RegistrationDates>
              {formatEventDate(null, status.registrationFrom, status.registrationTo)}
            </RegistrationDates>
          )}
          {status.registration === REGISTRATION_STATUS.OPEN && (
            <Button
              url={getRoute.series(slug).subscriptions()}
              leftIcon={ICON_NAME.playlistAddCheck}
              text={t('eventSidePanel_subscribeBtn.message')}
              urlScroll={false}
            />
          )}
        </SerieSidePanelSection>
      </SectionsContainer>

      <StackableCardSection>
        {urlOrganiser && (
          <Button
            url={{ pathname: urlOrganiser }}
            variant={BUTTON_VARIANT.link}
            leftIcon={ICON_NAME.computer}
            text={t('eventSidePanel_organiserLink.message')}
            urlTargetBlank
          />
        )}

        {previousSerieSlug && (
          <Button
            url={getRoute.series(previousSerieSlug).description()}
            variant={BUTTON_VARIANT.link}
            leftIcon={ICON_NAME.history}
            text={previousSerieLabel}
            urlScroll={false}
          />
        )}
      </StackableCardSection>
    </CardContainer>
  );
};

export default SerieSidePanel;

const CardContainer = styled(StackableCard)`
  width: 351px;
  padding: 10px;
  height: fit-content;
`;

const SectionsContainer = styled(StackableCardSection)`
  > *:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const SectionContainer = styled.div`
  display: grid;
  grid-column-gap: 8px;
  grid-row-gap: 8px;
  grid-template-columns: 20px auto;
  grid-template-areas: 
        "section-icon section-title"
        ". section-content";

  > *:not(:last-child) {
    color: var(--color-grey3);
  }
`;

const SectionIcon = styled(Icon)`
  grid-area: section-icon;
`;

const SectionTitle = styled.div`
  grid-area: section-title;
  ${textSmall};
  text-transform: uppercase;
`;

const SectionContent = styled.div`
  grid-area: section-content;
  ${textP};
  color: var(--color-grey4);
`;

const RegistrationDates = styled.p`
  margin-bottom: 10px;
`;
